import _ from "lodash";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";

export const Masonry = ({
  images,
  className,
  size,
}: {
  images: { width: number; height: number; id: string; render: ReactNode }[];
  className?: string;
  size?: "2" | "3";
}) => {
  const { site } = useParams<{ site: string }>();
  const gridRef = useRef<HTMLDivElement | null>(null);
  const defaultColumnWidth = 150;
  const [columnWidth, setColumnWidth] = useState(defaultColumnWidth);

  useEffect(() => {
    const updateColumnWidth = () => {
      if (gridRef.current) {
        const computedStyle = window.getComputedStyle(gridRef.current);
        const columnWidthValue = parseFloat(
          computedStyle.getPropertyValue("grid-template-columns").split(" ")[0]
        );
        setColumnWidth(columnWidthValue || defaultColumnWidth);
      }
    };

    updateColumnWidth();
    window.addEventListener("resize", _.debounce(updateColumnWidth, 1000));

    return () => {
      window.removeEventListener("resize", _.debounce(updateColumnWidth, 1000));
    };
  }, []);

  const sizes = {
    "2": [180, 250],
    "3": [250, 400],
  };

  return (
    <div
      ref={gridRef}
      className={twMerge("grid", className)}
      style={{
        gridAutoRows: "8px",
        gridTemplateColumns:
          site === "portraits"
            ? `repeat(auto-fill, minmax(${sizes[size || "3"][0]}px, 1fr))`
            : `repeat(auto-fill, minmax(${sizes[size || "3"][1]}px, 1fr))`,
      }}
    >
      {images?.map(({ height, width, id, render }) => (
        <div
          key={id}
          style={{
            gridRowEnd: `span ${Math.floor(
              (height / width) * (columnWidth / 8)
            )}`,
          }}
        >
          {render}
        </div>
      ))}
    </div>
  );
};

// Return either 2/3 3/2 or 1/1
export const getMasonrySize = (size: { width: number; height: number }) => {
  if (!size) {
    return { width: 2, height: 3, ratio: "1/1" };
  }
  const ratio = size.height / size.width;
  if (ratio > 1.2) {
    return { width: 2, height: 3, ratio: "2/3" };
  } else if (ratio < 0.8) {
    return { width: 3, height: 2, ratio: "3/2" };
  } else {
    return { width: 1, height: 1, ratio: "1/1" };
  }
};
